import * as React from 'react';
import { createStyled } from 'shared/hocs';
import { useQuery, useMutation } from 'react-apollo';
import {
  Theme,
  Grid,
  Paper,
  Typography,
  createStyles,
  Button as Btn,
} from '@material-ui/core';
import { USER_ALLOWANCE_QUERY, UserAllowanceQueryResult } from 'users/queries';
import {
  UPDATE_STRIPE_METADATA_MUTATION as UPDATE_STRIPE_PLAN_METADATA_MUTATION,
  UpdateStripeMetadataVars,
  UpdateStripePlanMetadataResult,
} from 'users/mutations';
import { createFormContext } from 'yafl';
import { getHourComponent } from 'shared/utils/time';
import { FormKit, FormSelectInput, FormTextInput } from 'shared/formKit';
import { LiveAllowance } from 'types';

const liveAllowanceContext = createFormContext<LiveAllowance>();
const { Field } = liveAllowanceContext;

interface Props {
  userId: string;
}

interface Data {
  user: UserAllowanceQueryResult;
}

// https://github.com/mui-org/material-ui/issues/11968
const Button: React.ComponentType<any> = Btn;

const LiveAllowanceForm = ({ userId }: Props) => {
  const [
    updateStripePlanMetadata,
    { loading: mutationLoading, error: mutationError },
  ] = useMutation<UpdateStripePlanMetadataResult, UpdateStripeMetadataVars>(
    UPDATE_STRIPE_PLAN_METADATA_MUTATION,
  );

  const { error, data, loading } = useQuery<Data, { userId: string }>(
    USER_ALLOWANCE_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      variables: { userId },
    },
  );

  const handleStripeMetadataUpdate = async (
    planId: string,
    metadataList: { key: string; value: string }[],
  ) => {
    try {
      console.log({ metadataList });
      await updateStripePlanMetadata({
        variables: {
          planId,
          metadataList,
        },
      });
      console.log('Stripe metadata updated successfully');
    } catch (error) {
      console.error('Error updating Stripe metadata:', error);
    }
  };

  if (error) return null;

  if (loading || !data || !data.user) return null;

  const { currentSubscription, group } = data.user;

  const { isEnterprise } = group || {};

  if (!isEnterprise) {
    return null;
  }

  const liveAllowanceInitialValues: LiveAllowance = {
    liveLimitType: currentSubscription?.liveLimitType || 'hard',
    liveBudgetPerUserHours: currentSubscription?.liveBudgetPerUserSeconds
      ? getHourComponent(
          currentSubscription.liveBudgetPerUserSeconds / 12,
        ).toString()
      : '0',
    liveTrialBudgetPerUserSeconds: currentSubscription?.liveTrialBudgetPerUserSeconds
      ? getHourComponent(
          currentSubscription.liveTrialBudgetPerUserSeconds,
        ).toString()
      : '0',
    livePurchased: currentSubscription?.livePurchased ? 'true' : 'false',
  };

  return (
    <FormKit<LiveAllowance>
      disabled={false}
      context={liveAllowanceContext}
      loading={loading || mutationLoading}
      initialValue={liveAllowanceInitialValues}
      onSubmit={(values) => {
        const planId = currentSubscription?.planId || '';
        const liveBudgetPerUserHours =
          parseInt(values.liveBudgetPerUserHours, 10) * 60 * 60;

        const metadata = [
          {
            key: 'mobileLiveBudgetPerUserPerMonthSeconds',
            value: liveBudgetPerUserHours.toString(),
          },
          {
            key: 'mobileLiveTrialBudget',
            value: liveBudgetPerUserHours.toString(),
          },
          {
            key: 'mobileLivePurchased',
            value: values.livePurchased,
          },
          { key: 'mobileLiveLimitType', value: values.liveLimitType },
        ];
        handleStripeMetadataUpdate(planId, metadata);
      }}
      components={{ input: FormTextInput }}
    >
      <Styled>
        {({ classes }) => (
          <main className={classes.layout}>
            <Paper className={classes.paper}>
              <Grid container spacing={24}>
                <Grid item xs={12}>
                  <h3>Live Allowance</h3>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    name="liveBudgetPerUserHours"
                    label="Live Budget per user per month (hours)"
                    component={FormTextInput}
                    type="number"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Field
                    name="livePurchased"
                    label="Paying for Live Hours?"
                    component={FormSelectInput}
                    options={[
                      {
                        value: 'true',
                        label: 'yes',
                      },
                      {
                        value: 'false',
                        label: 'no',
                      },
                    ]}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    name="liveLimitType"
                    label="Allowed Overages?"
                    component={FormSelectInput}
                    disabled={!isEnterprise}
                    options={[
                      {
                        value: 'soft',
                        label: 'yes',
                      },
                      {
                        value: 'hard',
                        label: 'no',
                      },
                    ]}
                  />
                </Grid>
                <Grid item xs={12} sm={6}></Grid>
              </Grid>
              {mutationError && (
                <Grid item xs={12}>
                  <Typography color="error">
                    Error updating live allowance: {mutationError.message}
                  </Typography>
                </Grid>
              )}
              <div className={classes.buttons}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  disabled={mutationLoading}
                >
                  {mutationLoading ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </Paper>
          </main>
        )}
      </Styled>
    </FormKit>
  );
};

const Styled = createStyled((theme: Theme) => {
  return createStyles({
    layout: {
      width: 'auto',
      marginLeft: theme.spacing.unit * 2,
      marginRight: theme.spacing.unit * 2,
      [theme.breakpoints.up(600 + theme.spacing.unit * 2 * 2)]: {
        width: 600,
        marginLeft: 'auto',
        marginRight: 'auto',
      },
    },
    paper: {
      marginTop: theme.spacing.unit * 3,
      marginBottom: theme.spacing.unit * 3,
      padding: theme.spacing.unit * 2,
      [theme.breakpoints.up(600 + theme.spacing.unit * 3 * 2)]: {
        marginTop: theme.spacing.unit * 6,
        marginBottom: theme.spacing.unit * 6,
        padding: theme.spacing.unit * 3,
      },
    },
    buttons: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    button: {
      marginTop: theme.spacing.unit * 3,
      marginLeft: theme.spacing.unit,
    },
    title: {
      marginBottom: theme.spacing.unit * 3,
    },
  });
});

export default LiveAllowanceForm;
